import { useEffect, useState } from "react";
import api, { formatError } from "../api";
import Header from "../components/Header";
import Podium from "../components/Podium";
import { FramedAvatar, rankTheme } from "../components/RankFrame";
import { toast } from "sonner";
import { Loader2, Trophy, Flame, Swords, Hash } from "lucide-react";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; }
}

function Highlight({ icon: Icon, label, h }) {
  if (!h) return null;
  return (
    <div className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-900/70 border border-slate-800">
      <Icon className="w-4 h-4 text-amber-400 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-mono">{label}</div>
        <div className="text-xs font-semibold text-slate-100 truncate">{h.name} <span className="text-amber-400 font-mono">{h.value}</span></div>
      </div>
    </div>
  );
}

function Standing({ r }) {
  const th = rankTheme(r.rank);
  return (
    <div data-testid={`season-row-${r.user_id}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${r.rank <= 3 ? "bg-slate-900/80 border-slate-700" : "bg-slate-900/40 border-slate-800/70"}`}>
      <div className="w-12 flex justify-center"><FramedAvatar icon={r.icon} name={r.name} rank={r.rank} size={r.rank <= 3 ? 34 : 30} crown={false} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-100 truncate flex items-center gap-1.5">{r.name}{th.medal && <span>{th.medal}</span>}</div>
        <div className="text-[11px] text-slate-500 font-mono">{r.wins}V · {r.losses}F · {r.last_places} sist · {r.win_pct}% · {r.matches_played} matcher</div>
      </div>
      <div className={`text-lg font-black font-mono ${r.rank <= 3 ? th.text : "text-amber-400/80"}`}>{r.rating}</div>
    </div>
  );
}

export default function Seasons() {
  const [archives, setArchives] = useState(null);

  useEffect(() => {
    api.get("/seasons")
      .then(async (r) => setArchives(await Promise.all(r.data.map((s) => api.get(`/seasons/${s.season}`).then((x) => x.data)))))
      .catch((e) => { toast.error(formatError(e.response?.data?.detail)); setArchives([]); });
  }, []);

  return (
    <div>
      <Header title="Säsonger" subtitle="Hall of Fame" />
      <div className="px-4 py-4 space-y-8">
        {archives === null && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>}
        {archives && archives.length === 0 && (
          <div className="text-center py-16 text-slate-500"><Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" />Ingen säsong är avslutad än.<br /><span className="text-xs">Slutställningen med pall och emblem visas här när en säsong är över.</span></div>
        )}
        {archives && archives.map((a) => {
          const w = a.standings[0];
          return (
            <section key={a.season} data-testid={`season-${a.season}`}>
              <div className="rounded-2xl p-4 bg-gradient-to-b from-amber-500/15 to-transparent border border-amber-500/30">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-2xl font-black font-display text-amber-300">Säsong {a.season}</h2>
                  <span className="text-[11px] text-slate-500 font-mono">{fmtDate(a.finished_at)}</span>
                </div>
                <p className="text-xs text-slate-400">{a.total_matches} matcher · {a.standings.length} spelare</p>
                {w && <p className="text-sm text-slate-200 mt-2">🏆 Grattis <b className="text-amber-300">{w.name}</b> – säsongens mästare!</p>}
                <Podium standings={a.standings} />
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <Highlight icon={Trophy} label="Flest vinster" h={a.highlights?.most_wins} />
                <Highlight icon={Flame} label="Bästa streak" h={a.highlights?.best_streak} />
                <Highlight icon={Swords} label="Flest sistaplatser" h={a.highlights?.most_last_places} />
                <Highlight icon={Hash} label="Flest matcher" h={a.highlights?.most_matches} />
              </div>

              <h3 className="text-sm font-bold font-display text-slate-200 mt-4 mb-2">Slutställning</h3>
              <div className="space-y-1.5">{a.standings.map((r) => <Standing key={r.user_id} r={r} />)}</div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
